import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarDays, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

/**
 * AgeGate — Phase F9.2: Nymia is 18+ only. Declared date of birth (NO KYC,
 * no documents, no biometrics). Age is computed server-side from the DOB.
 *  - DOB missing (legacy/social accounts) → mandatory blocking form.
 *  - DOB present and age < 18 → total block screen (logout only).
 * Solid opaque background: no app content visible behind the gate.
 * Excluded routes keep email verification / password recovery reachable.
 */
const EXCLUDED = ['/verify-email', '/forgot-password', '/reset-password'];

const AgeGate = () => {
  const { user, refresh, logout } = useAuth();
  const location = useLocation();
  const [dd, setDd] = useState('');
  const [mm, setMm] = useState('');
  const [yyyy, setYyyy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;
  if (EXCLUDED.some((p) => location.pathname.startsWith(p))) return null;

  // ── Under-18: TOTAL block (no feed, no nav, no content behind) ──────────
  if (user.date_of_birth && user.is_adult === false) {
    return (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-background p-4" data-testid="under18-block">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
            <ShieldAlert className="h-8 w-8 text-red-500" strokeWidth={1.5} />
          </div>
          <h2 className="text-lg font-bold" data-testid="under18-title">
            Nymia è disponibile solo per maggiorenni.
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Per utilizzare Nymia devi avere almeno 18 anni.
          </p>
          <Button
            onClick={logout}
            variant="outline"
            className="mt-6 w-full font-semibold"
            data-testid="under18-logout-btn"
          >
            Esci
          </Button>
        </div>
      </div>
    );
  }

  if (user.date_of_birth) return null;

  // ── DOB missing: mandatory declared date of birth ────────────────────────
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    if (!d || !m || !y || String(yyyy).length !== 4 || d > 31 || m > 12) {
      setError('Inserisci una data valida (GG / MM / AAAA).');
      return;
    }
    const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setSaving(true);
    try {
      await api.post('/auth/date-of-birth', { date_of_birth: iso });
      await refresh();
    } catch (err) {
      const raw = err.response?.data?.detail || '';
      setError(
        typeof raw === 'string' && raw.includes('|')
          ? raw.split('|')[1]
          : 'Data non valida. Controlla giorno, mese e anno.',
      );
    } finally {
      setSaving(false);
    }
  };

  const num = (setter, max) => (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, max);
    setter(v);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-background p-4" data-testid="age-gate">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
          <CalendarDays className="h-8 w-8 gold-text" strokeWidth={1.5} />
        </div>
        <h2 className="text-lg font-bold" data-testid="age-gate-title">Prima di continuare</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Nymia è riservata ai maggiorenni.
          <br />
          Inserisci la tua data di nascita.
        </p>
        <form onSubmit={submit} className="mt-5">
          <div className="flex items-center justify-center gap-2">
            <Input
              value={dd}
              onChange={num(setDd, 2)}
              placeholder="GG"
              inputMode="numeric"
              autoComplete="bday-day"
              className="h-11 w-16 text-center text-base"
              data-testid="age-gate-day"
            />
            <span className="text-muted-foreground">/</span>
            <Input
              value={mm}
              onChange={num(setMm, 2)}
              placeholder="MM"
              inputMode="numeric"
              autoComplete="bday-month"
              className="h-11 w-16 text-center text-base"
              data-testid="age-gate-month"
            />
            <span className="text-muted-foreground">/</span>
            <Input
              value={yyyy}
              onChange={num(setYyyy, 4)}
              placeholder="AAAA"
              inputMode="numeric"
              autoComplete="bday-year"
              className="h-11 w-24 text-center text-base"
              data-testid="age-gate-year"
            />
          </div>
          {error && (
            <p className="mt-3 text-xs font-semibold text-red-500" data-testid="age-gate-error">{error}</p>
          )}
          <Button
            type="submit"
            disabled={saving || !dd || !mm || yyyy.length !== 4}
            className="gold-gradient mt-5 w-full font-bold text-black"
            data-testid="age-gate-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continua'}
          </Button>
        </form>
        <button
          onClick={logout}
          className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline"
          data-testid="age-gate-logout-btn"
        >
          Esci
        </button>
      </div>
    </div>
  );
};

export default AgeGate;
